<template>
    <div class="desktop theater" :class="{ 'hide-cursor': !showControls }" @mousemove="handleMouseMove" >
        <!-- Top nav -->
        <div class="top-nav" v-show="showControls">
            <Back
                :goBack="goBack"
                :mode="mode"
            />
            <GTS
                :mode="mode"
                :type="type"
                :kind="kind"
                :league="league"
                :id="id"
            />
        </div>

        <portal-target name="wod-overlay"> </portal-target>

        <transition name="fade">
            <StackLoader ref="stack" v-if="showStackLoader"/>
        </transition>

        <transition name="fade">
            <PauseOverlay :media="media" :mode="mode" :loading="(ready && loading)" v-if="media" v-show="!playing"/>
        </transition>

        <transition name="fade">
            <div class="loader"  v-if="(ready && loading)">
                <Loader> </Loader>
            </div>
        </transition>

        <transition name="fade">
            <div class="toast error" v-if="linkErrorToast">
                <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" fill="none"> <path d="M12 7L12 13" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M12 17.01L12.01 16.9989" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                This stream is currently unavailable. Please try another.
            </div>
        </transition>

        <transition name="fade">
            <div class="toast success" v-if="challangeLinkToast">
                <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" fill="none"> <path d="M14 11.9976C14 9.5059 11.683 7 8.85714 7C8.52241 7 7.41904 7.00001 7.14286 7.00001C4.30254 7.00001 2 9.23752 2 11.9976C2 14.376 3.70973 16.3664 6 16.8714C6.36756 16.9525 6.75006 16.9952 7.14286 16.9952" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M10 11.9976C10 14.4893 12.317 16.9952 15.1429 16.9952C15.4776 16.9952 16.581 16.9952 16.8571 16.9952C19.6975 16.9952 22 14.7577 22 11.9976C22 9.6192 20.2903 7.62884 18 7.12383C17.6324 7.04278 17.2499 6.99999 16.8571 6.99999" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                Link copied to clipboard
            </div>
        </transition>

        <div class="iframe-container" v-if="mode === 'game'" :class="{allowpointer: enableIframePointers}">
            <iframe v-if="ready" v-show="gaming" allow="autoplay; fullscreen; gamepad" :src="url" ref="iframe" :class="{allowpointer: enableIframePointers}"/>
        </div>

        <div class="video-container" ref="videoContainer" v-else> </div>


        <AYSTW
            :show="stillWatchingPrompt"
            :yes="confirmStillWatching"
            :no="goBack"
        />

        <PickPlayer
            v-if="mode=== 'game'"
            :show="showPickPlayer"
            :playerSelected="playerSelected"
            :gameStarted="gameStarted"
        />

        <transition name="fade">
            <EndScreen
                v-if="endScreen"
                :type="type"
                :apc="apc"
                :goBack="goBack"
                :related="queue[0]"
                :close="goBack"
            />
        </transition>

        <div class="stream-menu-backdrop" v-if="showStreamMenu || showEpisodeMenu || showCCAMenu" @click="showStreamMenu = false; showEpisodeMenu = false; showCCAMenu = false"></div>

        <!-- Bottom control bar -->
        <div class="bottom-bar" v-show="showControls && !endScreen">
            <ProgressBar
                v-if="mode === 'od'"
                :buffered-percent="bufferedPercent"
                :progress-percent="progressPercent"
                :duration-formatted="durationFormatted"
                :hover-time-formatted="hoverTimeFormatted"
                :show-time-tooltip="showTimeTooltip"
                :hover-percent="hoverPercent"
                :hover-time="hoverTime"
                :thumbnails="thumbnails"
                @pointerdown="onProgressPointerDown"
                @pointermove="onProgressPointerMove"
                @pointerup="onProgressPointerUp"
                @pointercancel="onProgressPointerCancel"
                @pointerleave="onProgressPointerLeave"
            />

            <div class="controls">
                <div class="controls-group left" v-if="mode === 'game'">
                    <button class="icon-btn" type="button" aria-label="resync" @click="toggleIframePointer" >
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"> <path d="M20 10V14C20 18.4183 16.4183 22 12 22C7.58172 22 4 18.4183 4 14V9C4 5.13401 7.13401 2 11 2H12C16.4183 2 20 5.58172 20 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> <path d="M12 2V8.4C12 8.73137 11.7314 9 11.4 9H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>
                    </button>
                </div>
                <div class="controls-group left" v-else>
                    <button class="icon-btn" type="button" :aria-label="playing ? 'Pause' : 'Play'" @click="togglePlay">
                        <svg v-if="playing" viewBox="0 0 24 24" fill="currentColor"> <rect x="6" y="4" width="4" height="16" rx="1" /> <rect x="14" y="4" width="4" height="16" rx="1" /> </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor"> <polygon points="6 3 20 12 6 21 6 3" /> </svg>
                    </button>

                    <button class="icon-btn skip" type="button" aria-label="Rewind 10 seconds" @click="skip(-10)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <path d="M4 12a8 8 0 1 0 2.34-5.66" /> <polyline points="4 4 4 8 8 8" /> </svg>
                        <span class="skip-label">10</span>
                    </button>

                    <button class="icon-btn skip" type="button" aria-label="Forward 10 seconds" @click="skip(10)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <path d="M20 12a8 8 0 1 1-2.34-5.66" /> <polyline points="20 4 20 8 16 8" /> </svg>
                        <span class="skip-label">10</span>
                    </button>

                    <VolumeControl :volume="volume" @update:volume="volume = $event; updateVolume()" @toggle-mute="toggleMute" />
                </div>

                <div class="title">{{ title }}</div>

                <div class="controls-group right">

                    <button class="icon-btn" type="button" aria-label="resync" @click="resyncLive" v-if="isLive">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"> <path d="M17.5 8C17.5 8 19 9.5 19 12C19 14.5 17.5 16 17.5 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M20.5 5C20.5 5 23 7.5 23 12C23 16.5 20.5 19 20.5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.5 8C6.5 8 5 9.5 5 12C5 14.5 6.5 16 6.5 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M3.5 5C3.5 5 1 7.5 1 12C1 16.5 3.5 19 3.5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                    </button>

                    <button class="icon-btn" type="button" aria-label="resync" @click="reloadGameStream" v-if="mode === 'game'">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"> <path d="M17.5 8C17.5 8 19 9.5 19 12C19 14.5 17.5 16 17.5 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M20.5 5C20.5 5 23 7.5 23 12C23 16.5 20.5 19 20.5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.5 8C6.5 8 5 9.5 5 12C5 14.5 6.5 16 6.5 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M3.5 5C3.5 5 1 7.5 1 12C1 16.5 3.5 19 3.5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                    </button>

                    <button class="icon-btn" type="button" aria-label="resync" @click="copyChallengeLink" v-if="mode === 'game'">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"> <path d="M14 11.9976C14 9.5059 11.683 7 8.85714 7C8.52241 7 7.41904 7.00001 7.14286 7.00001C4.30254 7.00001 2 9.23752 2 11.9976C2 14.376 3.70973 16.3664 6 16.8714C6.36756 16.9525 6.75006 16.9952 7.14286 16.9952" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M10 11.9976C10 14.4893 12.317 16.9952 15.1429 16.9952C15.4776 16.9952 16.581 16.9952 16.8571 16.9952C19.6975 16.9952 22 14.7577 22 11.9976C22 9.6192 20.2903 7.62884 18 7.12383C17.6324 7.04278 17.2499 6.99999 16.8571 6.99999" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                    </button>

                    <button class="icon-btn" type="button" aria-label="next" @click="nextEpisode" v-if="type === 'tv' && !lastEp">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"> <path d="M18 7V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.97179 5.2672C6.57832 4.95657 6 5.23682 6 5.73813V18.2619C6 18.7632 6.57832 19.0434 6.97179 18.7328L14.9035 12.4709C15.2078 12.2307 15.2078 11.7693 14.9035 11.5291L6.97179 5.2672Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                    </button>

                    <EpisodeMenu
                        v-if="type === 'tv'"
                        :visible="showEpisodeMenu"
                        :seasons="seasons"
                        :current-season="season"
                        :current-episode="episode"
                        :menu-season="menuSeason"
                        :menu-episodes="menuEpisodes"
                        :menu-view="episodeMenuView"
                        @toggle="toggleEpisodeMenu"
                        @update:menuView="episodeMenuView = $event"
                        @change-season="changeSeason"
                        @change-episode="changeEpisode"
                    />

                    <RelatedMenu
                        v-else-if="mode !== 'game'"
                        :visible="showEpisodeMenu"
                        :mode="mode"
                        :related="queue"
                        @toggle="toggleEpisodeMenu"
                    />

                    <StreamMenu
                        v-if="mode !== 'game'"
                        :visible="showStreamMenu"
                        :streams="streams"
                        :active-stream="activeStream"
                        :playback-rates="playbackRates"
                        :current-playback-rate="currentPlaybackRate"
                        @toggle="toggleStreamMenu"
                        @change-stream="changeActiveStream"
                        @change-playback-rate="changePlaybackRate"
                    />

                    <CCAMenu
                        v-if="mode === 'od'"
                        :visible="showCCAMenu"
                        :audioTracks="audioTracks"
                        :subtitles="subtitles"
                        :activeAudioIndex="activeAudioIndex"
                        :activeSubtitleIndex="activeSubtitleIndex"

                        @toggle="toggleCCAMenu"
                        @change-audio="selectAudioTrack"
                        @change-subtitle="selectSubtitle"
                    />

                    <button class="icon-btn" type="button" aria-label="Fullscreen" @click="toggleFullscreen">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"> <path d="M7 4H4V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M17 4H20V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M7 20H4V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M17 20H20V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
                    </button>
                </div>
            </div>
        </div>

        <SkipIntro
            :show="skipIntroVisible"
            :highlight="forceShowSkipIntro"
            :skip="skipIntro"
        />
    </div>
</template>
<script>

const Back = () => import('@/components/theater/BackButton')
const EpisodeMenu = () => import('@/components/theater/EpisodeMenu')
const RelatedMenu = () => import('@/components/theater/RelatedMenu')
const StreamMenu = () => import('@/components/theater/StreamMenu')
const ProgressBar = () => import('@/components/theater/ProgressBar')
const VolumeControl = () => import('@/components/theater/VolumeControl')
const AYSTW = () => import('@/components/theater/AYSTW.vue')
const EndScreen = () => import('@/components/theater/EndScreen.vue')
const GTS = () => import('@/components/theater/GTS.vue')
const CCAMenu = () => import('@/components/theater/SubAndAudioMenu.vue')
const StackLoader = () => import('@/components/theater/StackedLoader')
const PauseOverlay = () => import('@/components/theater/PauseOverlay')
const SkipIntro = () => import('@/components/theater/SkipIntro.vue')
const PickPlayer = () => import('@/components/theater/PickPlayer.vue')
const Loader = () => import('@/components/Loader.vue')

import mpegts from 'mpegts.js';

export default {
    name: 'DesktopWod',
    components: {
        CCAMenu,
        GTS,
        AYSTW,
        EpisodeMenu,
        RelatedMenu,
        StreamMenu,
        ProgressBar,
        EndScreen,
        VolumeControl,
        PickPlayer,
        SkipIntro,
        Back,
        StackLoader,
        PauseOverlay,
        Loader
    },
    data() {
        return {
            ready: false,
            loading: false,
            linkErrorToast: false,
            challangeLinkToast: false,
            showStackLoader: true,

            cid: null,
            id: null,
            type: '',
            mode: null,
            kind: null,
            league: null,
            media: null,

            deadman: false,

            //player meta
            title: '',
            streams: [],
            activeStream: null,
            activeStreamIndex: 0,
            queue: [],
            sessionId: null,
            url: '',
            stillWatchingPrompt: false,
            stillWatchingTimeout: null,
            willPauseTimeout: null,
            apc: 0,
            endScreen: false,

            pickedPlayer: 1,
            showPickPlayer: false,
            gaming: false,
            enableIframePointers: false,

            //tv meta
            season: 1,
            episode: 1,
            seasons: 0,
            episodeCount: 0,
            episodes: [],

            menuSeason: 1,
            menuEpisodes: [],
            queryMode: 'filter',
            inProduction: false,
            queryModeAdjusted: false,
            name: '',
            poster: '',

            //hls state
            isSeekingTranscode: false,
            transcodeStartTime: 0,
            transcodeBuffered: 0,
            scrubbingProgress: false,
            thumbnails: null,
            intro: null,
            introId: null,


            activeSubtitleIndex: null,
            activeAudioIndex: null,

            subtitles: [],
            audioTracks: [],

            // player state
            playing: false,
            muted: false,
            currentTime: 0,
            duration: 0,
            buffered: 0,
            volume: 1,
            controlsTimeout: null,
            showControls: true,
            forceShowSkipIntro: false,
            skipIntroForceTimeout: null,

            isFullscreen: false,
            currentPlaybackRate: 1,
            playbackRates: [0.5, 1, 1.5, 2],
            showTimeTooltip: false,
            hoverPercent: 0,
            hoverTime: 0,
            showStreamMenu: false,
            showCCAMenu: false,

            showEpisodeMenu: false,
            episodeMenuView: 'episodes',

            player: null,
        }
    },
    computed: {
        lastEp(){
            return (this.season === this.seasons) && (this.episode === this.episodes.length)
        },
        isLive(){
            return this.mode === 'live' || this.mode === 'sport' || this.mode === 'epg'
        },
        showSkipIntro(){
            const i = this.intro
            return !!i && this.type === 'tv' && !this.endScreen && !this.isSeekingTranscode &&
                this.currentTime >= i.start && this.currentTime < i.end - 1
        },
        skipIntroVisible(){
            return this.showSkipIntro && (this.showControls || this.forceShowSkipIntro)
        },
        bufferedPercent() {
            if (!this.duration || !isFinite(this.duration) || this.duration <= 0) return 0;
            const percent = (this.transcodeBuffered / this.duration) * 100;
            return Math.min(100, Math.max(0, percent));
        },

        progressPercent() {
            if (!this.duration || !isFinite(this.duration) || this.duration <= 0) return 0;
            const dur = (this.currentTime / this.duration) * 100
            return dur
        },
        currentTimeFormatted() {
            return this.formatTime(this.currentTime);
        },
        durationFormatted() {
            return this.formatTime(this.duration - this.currentTime);
        },
        hoverTimeFormatted() {
            return this.formatTime(this.hoverTime);
        }

    },

    mounted() {
       this.startTheater()
    },

    beforeDestroy(){
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('keydown', this.handleKeyDown);
        this.clearSkipIntroForce();

        if(!this.ready || this.loading){
            this.deadman = true
            this.resetPlayer()
        }else{
            this.resetTheater()
        }
    },

    watch: {
        showSkipIntro(val) {
            if (val) {
                if (this.skipIntroForceTimeout) {
                    clearTimeout(this.skipIntroForceTimeout);
                }
                this.forceShowSkipIntro = true;
                this.skipIntroForceTimeout = setTimeout(() => {
                    this.forceShowSkipIntro = false;
                    this.skipIntroForceTimeout = null;
                }, 5000);
            } else {
                this.clearSkipIntroForce();
            }
        },
        $route: async function() {
            if(this.$route.path.includes('theater')){
                await this.resetTheater()
                await this.$nextTick()
                await this.$nextTick()
                this.startTheater()
            }else{
                if(!this.ready || this.loading){
                    this.deadman = true
                }else{
                    await this.resetTheater()
                }
            }

        },
        showStreamMenu(val) {
            if (val) {
                clearTimeout(this.controlsTimeout);
            } else {
                this.resetControlsTimeout();
            }
        },
        showEpisodeMenu(val) {
            if (val) {
                clearTimeout(this.controlsTimeout);
            } else {
                this.resetControlsTimeout();
            }
        }
    },

    methods: {
        startTheater(){
            this.setFromQuery()

            if(this.mode === 'live'){ this.loadLive() }
            if(this.mode === 'sport'){ this.loadSport() }
            if(this.mode === 'epg'){ this.loadEPG() }
            if(this.mode === 'od' && this.type === 'movie'){ this.loadMovie() }
            if(this.mode === 'od' && this.type === 'tv'){ this.loadShow() }
            if(this.mode === 'game'){ this.loadGame() }

            window.addEventListener('beforeunload', this.handleBeforeUnload);
            window.addEventListener('keydown', this.handleKeyDown);
        },

        setFromQuery() {
            const { id, mode, type, league, kind, apc } = this.$route.query
            this.id = id || null
            this.mode = mode || null
            this.type = type || ''
            this.league = league
            this.kind = kind
            this.apc = apc ? parseInt(apc) : 0

            this.cid = window.localStorage.getItem('cid')
        },

        async loadGame(){
            //maybe make special epoint to 1shot stream status + game data + stream url / ip check
            const req = await window.afetch('/game?id='+ this.id)
            const data = await req.json()

            this.title = data.game.title
            this.queue = data.related // we dont shit with this atm
            document.title = `${this.title} - QB`
            this.setMetaData(data.game.title, data.game.coverArt)

            const activeLobby = await window.afetch('/gameParty')
            const alData = await activeLobby.json()

            if(!alData.active){
                await window.afetch('/party/openLobby', {method: 'POST', body: JSON.stringify({ ...data.game }) })
            }

            const req2 = await window.afetch('/game/streamurl')
            const data2 = await req2.json()
            window.wrFrameURL = data2.stream


            this.showPickPlayer = true
        },

        async loadLive(){
            this.pushLoaderMessage('Loading channel')
            const req = await window.afetch(`/channel?id=${this.id}`)
            const data = await req.json()
            this.media = data
            this.title = data.name
            this.queue = data.related
            this.streams = data.links
            this.activeStream = data.links[0]
            document.title = `${this.title} - QB`
            this.initPlayer(this.activeStream.link)
            this.setMetaData(data.name, data.img)
        },

        async loadSport(){
            this.pushLoaderMessage('Loading channel')
            const req = await window.afetch(`/sport?sport=${this.$route.query.kind}&league=${this.$route.query.league}&eventId=${this.id}`)
            const data = await req.json()
            this.media = data
            this.title = data.name
            this.queue = data.related
            this.streams = data.streams
            this.activeStream = data.streams[0]
            document.title = `${this.title} - QB`
            this.initPlayer(this.activeStream.link)
            this.setMetaData(data.name, data.sportLogo)
        },

        async loadEPG(){
            this.pushLoaderMessage('Loading channel')
            const req = await window.afetch(`/epgChannel?id=${this.id}`)
            const data = await req.json()
            this.media = data.channels[0]
            this.title = data.channels[0].name
            this.queue = data.related
            this.streams = data.channels.reduce((a, b) => { return [...a, ...b.links]}, [])
            this.activeStream = this.streams[0]
            document.title = `${this.title} - QB`
            this.initPlayer(this.activeStream.link)
            this.setMetaData(data.name, data.channels[0].img)
        },

        async loadMovie(){
            this.pushLoaderMessage('Loading details')

            const response  = await window.afetch(`/details?id=${this.id}&type=${this.type}`)
            const data =  await response.json()
            this.media = data

            this.title = `${data.title}`
            document.title = `${data.title} - QB`

            this.saveToContinueWatchingHistory(data)

            this.queue = data.reccomended

            this.pushLoaderMessage('Finding streams')
            const params = new URLSearchParams({
                title: data.title,
                type: this.type,
                year: data.year,
                id: this.id,
            });

            const streamres = await window.afetch(`/streams?${params}`);
            const streamdata = await streamres.json()
            this.streams =  streamdata.streams.map((x) => { return {name: x.quality, fullname: x.title, link: x.url}})
            this.activeStream = this.streams[0]

            this.initPlayer(this.activeStream.link)

            this.setMetaData(data.title, data.poster_path)
        },

        async loadShow(){
            this.pushLoaderMessage('Loading details')
            const response  = await window.afetch(`/details?id=${this.id}&type=${this.type}`)
            const data =  await response.json()
            this.media = data

            this.saveToContinueWatchingHistory(data)

            //capture fixed meta
            this.seasons =  data.number_of_seasons
            this.episodeCount = data.number_of_episodes
            this.queue = data.reccomended
            this.poster = data.poster_path
            this.name = data.name
            this.inProduction = data.in_production

            //maybe adjust based on other factors like season count
            //expose manual control somewhere?
            this.queryMode = data.in_production ? 'query' : 'filter'

            await this.readFromLocalStorage(data)

            //dynamic meta based on progress
            this.episodes = await this.getSeason(this.season)
            this.menuSeason = this.season
            this.menuEpisodes = this.episodes

            await this.$nextTick()

            await this.loadEpisode()

            this.startWatchingTimer()

        },

        async loadEpisode(){
            this.showStackLoader = true

            const ep = this.episodes[this.episode - 1]
            const [y, m, d] = String((ep && ep.air_date) || '').split('-').map(Number)
            const airDate = y && m && d ? new Date(y, m - 1, d) : null
            const now = new Date()

            if (airDate && airDate > new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
                this.showStackLoader = true
                this.pushLoaderMessage(
                    `Episode ${this.episode} airs on ${airDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                )
                this.title = ``
                return
            }

            this.pushLoaderMessage('Loading episode')

            this.title = `${this.name} E${this.episode} | ${ep.name}`
            document.title = `${this.title} - QB`

            this.pushLoaderMessage('Loading stream')
            const params = new URLSearchParams({
                mode: this.queryMode,
                title: this.name,
                type: this.type,
                e: this.episode,
                s: this.season,
                count: this.episodeCount,
                id: this.id,
            });

            const streamres = await window.afetch(`/streams?${params}`);
            const streamdata = await streamres.json()

            if(!streamdata || streamdata.streams.length < 1){
                //hail marry when cant find shi
                if(this.queryModeAdjusted && this.queryMode === 'disc'){
                    this.queryMode = 'query'
                    return this.loadEpisode()
                }
                if(!this.queryModeAdjusted){
                    this.queryMode = this.inProduction ? 'filter' : 'disc'
                    this.queryModeAdjusted = true
                    return this.loadEpisode()
                }
            }
            this.streams = streamdata.streams.map((x) => { return {name: x.quality, fullname: x.title, link: x.url}})
            this.activeStream = this.streams[this.activeStreamIndex]

            await this.initPlayer(this.activeStream.link)
            this.setMetaData(this.name, this.poster)
        },

        async readFromLocalStorage(data){
            let activeEpisodes = data?.season?.episodes || []
            const storedList = JSON.parse(localStorage.getItem('WODHistory')) || [];
            const storedEntry = storedList.find(item => item.id == this.id);

            let chosenSeason = data?.history?.season || 0;
            let chosenEpisode = data?.history?.episode || 0;

            if(storedEntry){
                this.episode = parseInt(storedEntry.episode)
                this.season = parseInt(storedEntry.season)
                activeEpisodes = await this.getSeason(this.season)
            }

            if (storedEntry && (storedEntry.season && storedEntry.season > chosenSeason) || (storedEntry && storedEntry.episode > chosenEpisode)) {
                chosenEpisode = storedEntry.episode;
                chosenSeason = storedEntry.season;
            }
            if (chosenEpisode > 0) {
                let nextSeason = chosenSeason;
                let nextEpisode = chosenEpisode + 1;

                if (nextEpisode > activeEpisodes.length) {
                    if (chosenSeason < this.seasons) {
                        nextSeason = chosenSeason + 1;
                        nextEpisode = 1;
                    } else {
                        nextSeason = chosenSeason;
                        nextEpisode = chosenEpisode;
                    }
                }

                this.season = nextSeason
                this.episode = nextEpisode

            }else{
                this.season = 1
                this.episdoe = 1
            }
        },

        recordEpisodeInLocalStorage() {
            const obj = {
                id: this.id,
                episode: this.episode,
                season: this.season
            };

            const STORAGE_KEY = 'WODHistory';
            const list = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
            const existingIndex = list.findIndex(item => item.id === obj.id);

            if (existingIndex !== -1) {
                list[existingIndex] = obj;
            } else {
                list.push(obj);
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        },

        recordEpisodeAsDone(){
            if(this.mode === 'od'){
                if(this.duration > 0 && this.currentTime > 0){
                    if(this.currentTime / this.duration > 0.6){
                        this.recordEpisodeInLocalStorage();
                    }
                }
            }
        },

        saveToContinueWatchingHistory(media) {
            let continueWatching = JSON.parse(localStorage.getItem('continueWatching')) || [];

            continueWatching = continueWatching.filter(item => item.id !== media.id);

            continueWatching.unshift(media);

            if (continueWatching.length > 20) {
                continueWatching = continueWatching.slice(0, 20);
            }

            localStorage.setItem('continueWatching', JSON.stringify(continueWatching));
        },

        async getSeason(season){
            const response  = await window.afetch(`/season?id=${this.id}&type=${this.type}&season=${season}`)
            const data =  await response.json()
            return data.episodes
        },

        async initIPTVPlayer(url, videoElement) {
            this.pushLoaderMessage('Connecting to live stream')
            const iptvUrl = `${process.env.VUE_APP_PROXY}/iptv-start`;

            const d = await fetch(iptvUrl, {method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({url: url, cid: this.cid })
            })
            const data  = await d.json()

            const videoSrc = data.url;
            this.url = videoSrc;
            this.sessionId = data.sessionId;

            if (mpegts.isSupported() && !this.deadman) {
                this.mpegtsPlayer = mpegts.createPlayer({
                    type: 'mpegts',
                    isLive: true,
                    url: videoSrc
                });
                this.mpegtsPlayer.attachMediaElement(videoElement);
                this.mpegtsPlayer.load();
                this.mpegtsPlayer.play();
                this.mpegtsPlayer.on(mpegts.Events.ERROR, (errorType, errorDetail) => {
                    console.log('mpegts error', errorType, errorDetail);
                    if (errorType === mpegts.ErrorTypes.NETWORK_ERROR) {
                        const currentTime = videoElement.currentTime;
                        this.mpegtsPlayer.unload();
                        this.mpegtsPlayer.load();
                        videoElement.currentTime = currentTime;
                        this.mpegtsPlayer.play();
                    }
                });
            }
            this.pushLoaderMessage('Ready! Press play.')
        },

        async initVODPlayer(mag){
            this.intro = null
            this.introId = null
            this.pushLoaderMessage('Listen here maine')
            //download support somewhere
            const response = await window.afetch(`/mag`, {
                method: 'POST',
                body: JSON.stringify({
                    noArchive: true,
                    mag: mag,
                    e: this.episode,
                    s: this.season
                })
            });
            const streamURLData = await response.text();
            const streamURLNotAvail = (streamURLData === null || streamURLData === undefined || streamURLData === '')
            if(streamURLNotAvail){
                if (this.activeStreamIndex >= this.streams.length - 1) {
                    this.pushLoaderMessage('Its so over')
                    this.linkErrorToast = true
                    return;
                }

                this.pushLoaderMessage('Trying another stream')
                const index = this.streams.indexOf(this.activeStream);
                this.activeStream = this.streams[(index + 1) % this.streams.length];

                this.$nextTick()
                this.resyncLive()
            }

            this.loading = true
            this.url = streamURLData;

            this.pushLoaderMessage('Connecting stream')
            const videoSrc = `${process.env.VUE_APP_PROXY}/transcode-video`;
            const d = await fetch(videoSrc, {method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({url: streamURLData, cid: this.cid, detectIntro: this.type === 'tv'})
            })

            const data  = await d.json()

            this.duration = data.duration;
            this.thumbnails = data.thumbnails || null;
            this.fetchIntro(data.introId);
            this.sessionId = data.sessionId;
            this.subtitles = data.subtitleTracks
            this.audioTracks = data.audioTracks
            this.activeAudioIndex = data.audioIndex
            this.activeSubtitleIndex = null //data.subtitleIndex

            this.player.src({
                src: data.playlist,
                type: this.getVideoType(data.playlist)
            });

            this.pushLoaderMessage('Ready! Press play.')
        },

        async initPlayer(videoSrc) {
            this.pushLoaderMessage('Initializing Player...')
            this.linkErrorToast = false
            if (this.$refs.videoContainer) {
                while (this.$refs.videoContainer.firstChild) {
                    this.$refs.videoContainer.removeChild(this.$refs.videoContainer.firstChild);
                }
            }

            let videoElement = document.createElement('video');

            videoElement.className = 'video-js vjs-big-play-centered';
            videoElement.setAttribute('playsinline', '');

            videoElement.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleVideoClick();
            });

            this.$refs.videoContainer.appendChild(videoElement);

            this.player = window.videojs(videoElement, {
                controls: false,
                autoplay: true,
                preload: 'auto',
                fluid: true,
                responsive: true,
                textTrackDisplay: {
                    allowMultipleShowingTracks: false
                },
                html5: {
                    vhs: {
                        overrideNative: true,
                        withCredentials: false
                    },
                    nativeTextTracks: true,
                    nativeAudioTracks: false,
                    nativeVideoTracks: false
                }
            });

            if(this.isLive){
                await this.initIPTVPlayer(videoSrc, videoElement)
            } else{
                await this.initVODPlayer(videoSrc)
            }

            // Event listeners
            this.player.one('ready', () => {
                if(this.deadman){ this.resetTheater() }
                if(this.mode === 'od'){
                    this.player.currentTime(0)
                }
            })

            this.player.one('play', () => {
                this.updateVolume()
                this.showStackLoader = false
                this.loading = false
            });

            this.player.on('play', () => {
                this.playing = true;
                this.resetControlsTimeout();
            });

            this.player.on('pause', () => {
                this.playing = false;
                this.showControls = true;
                clearTimeout(this.controlsTimeout);
            });

            if(this.type === 'tv'){
                this.player.on('ended', () => {
                    if(!this.lastEp){
                        this.nextEpisode()
                    }else{
                        this.stopWatchingTimer()
                        this.endScreen = true
                    }
                });
            }
            if(this.type === 'movie'){
                this.player.on('ended', () => {
                    this.endScreen = true
                });
            }

            this.player.on('timeupdate', () => {
                if(this.isSeekingTranscode) return;
                this.currentTime = this.player.currentTime() + this.transcodeStartTime;

                if (this.player.buffered().length > 0) {
                    const bufferedEnd = this.player.buffered().end(this.player.buffered().length - 1);
                    this.transcodeBuffered = this.transcodeStartTime + bufferedEnd;
                }
            });

            //implement subs
            this.player.ready(() => {
                if(this.deadman){ this.resetTheater() }
                this.ready = true
            });

            this.resetControlsTimeout();
        },

        getVideoType(url) {
            if (url.includes('.m3u8')) return 'application/x-mpegURL';
            return 'video/mp4';
        },

        handleVideoClick() {
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
                this.clickTimeout = null;
                this.toggleFullscreen();
            } else {
                this.clickTimeout = setTimeout(() => {
                    if (this.player) {
                        this.togglePlay();
                    }
                    this.clickTimeout = null;
                }, 250);
            }
        },

        handleKeyDown(e) {
            const activeEl = document.activeElement;
            const isTypingTarget = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable);
            if (isTypingTarget) return;

            if (e.code === 'Space') {
                e.preventDefault();
                if (this.forceShowSkipIntro && this.showSkipIntro) {
                    this.skipIntro();
                } else {
                    this.togglePlay();
                }
                return;
            }

            if (e.code === 'ArrowLeft') {
                e.preventDefault();
                this.player.currentTime(Math.max(0, this.player.currentTime() - 5));
                this.resetControlsTimeout();
                return;
            }

            if (e.code === 'ArrowRight') {
                e.preventDefault();
                this.player.currentTime(Math.min(this.duration, this.player.currentTime() + 5));
                this.resetControlsTimeout();
                return;
            }

            if (e.code === 'ArrowUp') {
                e.preventDefault();
                this.volume = Math.min(1, Math.round((this.volume + 0.1) * 100) / 100);
                this.updateVolume();
                return;
            }

            if (e.code === 'ArrowDown') {
                e.preventDefault();
                this.volume = Math.max(0, Math.round((this.volume - 0.1) * 100) / 100);
                this.updateVolume();
                return;
            }
        },

        setMetaData(title, artwork) {
            if ('mediaSession' in navigator) {
                navigator.mediaSession.metadata = new window.MediaMetadata({
                    title: title,
                    artwork: [{ src: artwork, type: 'image' }]
                });
            }
        },

        //run before leaving beacon send off
        handleBeforeUnload() {
            try {
                if(this.mode === 'game'){ return }

                this.recordEpisodeAsDone()

                if (this.sessionId) {
                    const url = this.isLive
                        ? `${process.env.VUE_APP_PROXY}/iptv-stop?sessionId=${this.sessionId}`
                        : `${process.env.VUE_APP_PROXY}/transcode-stop?sessionId=${this.sessionId}`;
                    window.navigator.sendBeacon(url);
                } else if (this.cid) {
                    const url = this.isLive
                        ? `${process.env.VUE_APP_PROXY}/iptv-client-stop?clientId=${this.cid}`
                        : `${process.env.VUE_APP_PROXY}/transcode-stop?clientId=${this.cid}`;
                    window.navigator.sendBeacon(url);
                }
            } catch (e) {
                console.warn('stopSession beacon failed:', e);
            }
        },
        //manually stop session for await
        async stopSession() {
            if(this.mode === 'game'){ return }
                if (this.sessionId) {
                    const url = this.isLive
                        ? `${process.env.VUE_APP_PROXY}/iptv-stop?sessionId=${this.sessionId}`
                        : `${process.env.VUE_APP_PROXY}/transcode-stop?sessionId=${this.sessionId}`;
                    await window.fetch(url, { method: 'POST'})
                } else if (this.cid) {
                    const url = this.isLive
                        ? `${process.env.VUE_APP_PROXY}/iptv-client-stop?clientId=${this.cid}`
                        : `${process.env.VUE_APP_PROXY}/transcode-stop?clientId=${this.cid}`;
                    await window.fetch(url, { method: 'POST'})
                }
        },

        async goBack() {
            if(this.mode === 'game'){ window.afetch(`/stopGameStream`) }
            this.endScreen = false
            const holdislive = this.isLive
            const holdmode = this.mode

            this.recordEpisodeAsDone()

            await this.resetTheater()

            if(holdislive){
                this.$router.push('/live')
            }else if(holdmode === 'game'){

                this.$router.push('/games')
            }else{
                this.$router.push('/')
            }
        },

        togglePlay() {
            if(!this.player){return}
            if (this.player.paused()) {
                this.player.play();
                if(this.type === 'tv'){ this.startWatchingTimer() }
            } else {
                this.player.pause();
                if(this.type === 'tv'){ this.stopWatchingTimer() }
            }
            this.resetControlsTimeout();
        },

        skip(seconds) {
            if(!this.player){return}
            this.player.currentTime(Math.max(0, this.player.currentTime() + seconds));
            this.resetControlsTimeout();
        },

        seek(event) {
            if (this.isSeekingTranscode) return;

            const rect = event.currentTarget.getBoundingClientRect();
            const percent = (event.clientX - rect.left) / rect.width;
            const seekTime = percent * this.duration;

            this.$root.$emit('theaterEvent');

            if (this.url) {
                const buffered = this.player.buffered();
                let isWithinBuffer = false;

                for (let i = 0; i < buffered.length; i++) {
                    const start = this.transcodeStartTime + buffered.start(i);
                    const end = this.transcodeStartTime + buffered.end(i);

                    if (seekTime >= start && seekTime <= end) {
                        isWithinBuffer = true;
                        break;
                    }
                }

                if (!isWithinBuffer) {
                    this.seekTranscode(seekTime);
                    return
                }

                this.player.currentTime(seekTime);
                return;
            }
            // Normal seek for non-HLS
            this.player.currentTime(seekTime);
            this.currentTime = seekTime;
        },

        async fetchIntro(introId) {
            this.introId = introId || null
            if (!introId) { return }

            // server long-polls up to ~20s; retry a few times if detection is still running
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const res = await fetch(`${process.env.VUE_APP_PROXY}/transcode-intro/${introId}`)
                    const data = await res.json()
                    if (this.introId !== introId) { return } // episode changed while waiting
                    if (!data.pending) {
                        this.intro = data.intro || null
                        return
                    }
                } catch (e) {
                    return
                }
            }
        },

        skipIntro() {
            if (!this.player || !this.intro) { return }

            this.clearSkipIntroForce();

            // player.currentTime() is relative to the start of the current transcode
            const target = this.intro.end
            const relative = target - this.transcodeStartTime
            const buffered = this.player.buffered()
            const bufferedEnd = buffered.length ? buffered.end(buffered.length - 1) : 0

            if (relative >= 0 && relative <= bufferedEnd) {
                this.player.currentTime(relative)
            } else {
                this.seekTranscode(target)
            }
            this.resetControlsTimeout();
        },

        async seekTranscode(seekTime) {
            if (!this.url || this.isSeekingTranscode || !this.player) return;


            this.isSeekingTranscode = true;
            this.player.pause();
            this.currentTime = seekTime;

            try {
                this.loading = true
                const response = await fetch(process.env.VUE_APP_PROXY + '/transcode-seek', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        sessionId: this.sessionId,
                        seekTime: seekTime,
                        videoUrl: this.url,
                        audioIndex: this.activeAudioIndex,
                        subtitleIndex: this.activeSubtitleIndex,
                        cid: this.cid
                    })
                });

                const data = await response.json();

                if (data.playlist) {
                    this.sessionId = data.sessionId;
                    this.transcodeStartTime = seekTime;
                    this.transcodeBuffered = seekTime;
                    this.activeSubtitleIndex = data.subtitleIndex;

                    this.player.src({
                        src: data.playlist,
                        type: 'application/x-mpegURL'
                    });

                    this.isSeekingTranscode = false;
                    this.loading = false
                }
            } catch (error) {
                console.error('Seek transcode failed:', error);
                this.isSeekingTranscode = false;
            }
        },

        updateHoverTime(event) {
            if(this.duration === 0){
                this.showTimeTooltip = false;
                return
            }
            const rect = event.currentTarget.getBoundingClientRect();
            this.hoverPercent = ((event.clientX - rect.left) / rect.width) * 100;
            this.hoverTime = (this.hoverPercent / 100) * this.duration;
            this.showTimeTooltip = true;
        },

        hideHoverTime() {
            this.showTimeTooltip = false;
            this.hoverPercent = 0;
        },

        onProgressPointerDown(event) {
            if (this.isSeekingTranscode) return;
            this.scrubbingProgress = true;
            this.$root.$emit('theaterEvent');
            this.resetControlsTimeout();
            if (event.currentTarget.setPointerCapture) {
                try { event.currentTarget.setPointerCapture(event.pointerId); } catch (e) { /* noop */ }
            }
            this.updateHoverTime(event);
        },

        onProgressPointerMove(event) {
            if (this.scrubbingProgress || event.pointerType === 'mouse') {
                this.updateHoverTime(event);
                this.resetControlsTimeout();
            }
        },

        onProgressPointerUp(event) {
            if (!this.scrubbingProgress) return;
            this.scrubbingProgress = false;
            this.seek(event);
            this.resetControlsTimeout();
            if (event.pointerType !== 'mouse') {
                this.hideHoverTime();
            }
        },

        onProgressPointerCancel() {
            this.scrubbingProgress = false;
            this.hideHoverTime();
        },

        onProgressPointerLeave() {
            if (!this.scrubbingProgress) {
                this.hideHoverTime();
            }
        },

        toggleMute() {
            if(!this.player){ return }

            if (this.player.muted()) {
                this.player.muted(false);
                this.volume = this.player.volume();
            } else {
                this.player.muted(true);
                this.volume = 0;
            }
            this.resetControlsTimeout();

        },

        updateVolume() {
            this.player.volume(this.volume);
            this.player.muted(this.volume === 0);
            this.resetControlsTimeout();
        },

        changePlaybackRate(rate) {
            this.player.playbackRate(rate);
            this.currentPlaybackRate = rate;
            this.showStreamMenu = false
        },

        async reloadGameStream(){
            await window.afetch(`/game/reloadweb`)
        },

        copyChallengeLink() {
            const url = `https://oc.studio14a.com/game/${this.id}/challenge`;
            navigator.clipboard.writeText(url);
            this.challangeLinkToast = true;
            setTimeout(() => {
                this.challangeLinkToast = false;
            }, 3000);
        },

        toggleIframePointer(){
            this.enableIframePointers = !this.enableIframePointers
        },

        playerSelected(player) {
            this.pickedPlayer =  player // 1 - 4
            this.url =  `${window.wrFrameURL}&player=${this.pickedPlayer}`
        },

        gameStarted(){
            this.showPickPlayer = false
            this.ready = true
            this.gaming = true
            this.resetControlsTimeout();
        },

        async resyncLive(){
            await this.resetPlayer()
            await this.$nextTick();
            await this.$nextTick();
            this.initPlayer(this.activeStream.link);
        },

        async resetPlayer() {
            this.showStackLoader = true
            this.playing = false
            this.ready = false;

            this.duration = 0
            this.currentTime = 0
            this.transcodeBuffered = 0
            this.transcodeStartTime = 0
            this.thumbnails = null
            this.intro = null
            this.introId = null

            await this.stopSession();

            if (this.mpegtsPlayer) {
                try {
                    this.mpegtsPlayer.pause();
                    this.mpegtsPlayer.unload();
                    this.mpegtsPlayer.detachMediaElement();
                    this.mpegtsPlayer.destroy();
                } catch (e) {
                    console.warn('mpegts cleanup error:', e);
                }
                this.mpegtsPlayer = null;
            }

            if (this.player) {
                try {
                    this.player.pause();
                    this.player.reset();
                    this.player.dispose();
                } catch (e) {
                    console.warn('videojs cleanup error:', e);
                }
                this.player = null;
            }

            if (this.$refs.videoContainer) {
                while (this.$refs.videoContainer.firstChild) {
                    this.$refs.videoContainer.removeChild(this.$refs.videoContainer.firstChild);
                }
            }

            clearTimeout(this.controlsTimeout);
            clearTimeout(this.clickTimeout);
            this.controlsTimeout = null;
            this.clickTimeout = null;
            this.clearSkipIntroForce();

            this.sessionId = null;

        },

        async resetTheater(){
            if(this.mode === 'game'){
                return
            }
            await this.resetPlayer()
            await this.$nextTick()
            await this.$nextTick()

            window.removeEventListener('beforeunload', this.handleBeforeUnload);
            window.removeEventListener('keydown', this.handleKeyDown);

            this.loading = false
            this.cid = null
            this.id = null
            this.type = ''
            this.mode = null
            this.league = null
            this.kind = null
            this.linkErrorToast = false
            this.challangeLinkToast = false
            this.showStackLoader = true
            this.media = null

            //player meta
            this.title = ''
            this.streams = []
            this.activeStream = null
            this.activeStreamIndex = 0
            this.queue = []
            this.sessionId = null
            this.url = ''
            this.stillWatchingPrompt = false
            this.stillWatchingTimeout = false
            this.willPauseTimeout = false
            this.endScreen = false
            this.pickedPlayer = 1
            this.showPickPlayer = false
            this.gaming = false
            this.enableIframePointers = false

            this.season = 1
            this.episode = 1
            this.seasons = 0
            this.episodeCount = 0
            this.episodes = []
            this.menuSeason = 1
            this.menuEpisodes = []
            this.queryMode = 'filter'
            this.inProduction = false
            this.queryModeAdjusted = false
            this.name = ''
            this.poster = ''

            //hls state
            this.isSeekingTranscode = false
            this.transcodeStartTime = 0
            this.transcodeBuffered = 0
            this.scrubbingProgress = false
            this.thumbnails = null


            this.subtitles = []
            this.audioTracks = []
            this.activeSubtitleIndex = null
            this.activeAudioIndex = null

            // player state
            this.playing = false
            this.muted = false
            this.currentTime = 0
            this.duration = 0
            this.buffered = 0
            this.volume = 1
            this.controlsTimeout = null
            this.showControls = true

            this.isFullscreen = false
            this.currentPlaybackRate = 1
            this.showTimeTooltip = false
            this.hoverPercent = 0
            this.hoverTime = 0
            this.showStreamMenu = false
            this.showCCAMenu = false
            this.showEpisodeMenu = false
            this.episodeMenuView = 'episodes'

            this.player = null

            this.stopWatchingTimer()

        },

        toggleCCAMenu() {
            this.showCCAMenu = !this.showCCAMenu
            if(this.showCCAMenu){
                this.showEpisodeMenu = false
                this.showStreamMenu = false
            }
        },

        toggleStreamMenu() {
            this.showStreamMenu = !this.showStreamMenu
            if(this.showStreamMenu){
                this.showCCAMenu = false
                this.showEpisodeMenu = false
            }
        },

        changeActiveStream(stream) {
            this.activeStream = stream
            this.showStreamMenu = false
            this.resyncLive()

            this.loading = true
            this.ready = true

            if(this.type === 'tv'){
                this.activeStreamIndex = this.streams.findIndex(s => s.link === stream.link)
            }
        },

        async toggleEpisodeMenu() {
            this.showEpisodeMenu = !this.showEpisodeMenu
            if(this.showEpisodeMenu){
                this.episodeMenuView = 'episodes'
                this.showStreamMenu = false
                this.showCCAMenu = false

                if(this.menuSeason !== this.season){
                    this.menuSeason = this.season
                    this.episodes = await this.getSeason(this.menuSeason)
                }
            }
        },

        async changeSeason(season) {
            this.queryMode = this.inProduction ? 'query' : 'filter'
            this.queryModeAdjusted = false
            this.menuSeason = season
            //if current season just use existing this.episodes
            this.menuEpisodes = await this.getSeason(season)
            this.episodeMenuView = 'episodes'
        },


        async changeEpisode(s, e){
            this.showEpisodeMenu = false
            await this.resetPlayer()
            this.$nextTick()
            this.$nextTick()
            this.episode = e
            this.season = s
            this.episodes = await this.getSeason(s)
            this.$nextTick()
            this.loadEpisode()
        },

        async nextEpisode(){
            this.recordEpisodeInLocalStorage();
            await this.readFromLocalStorage()
            await this.$nextTick()
            await this.resetPlayer()
            await this.$nextTick()
            this.loadEpisode()
        },

        async selectSubtitle(index) {
            if (!this.player || this.activeSubtitleIndex === index) {
                this.showSubtitlesMenu = false;
                return;
            }

            this.activeSubtitleIndex = index;
            this.showSubtitlesMenu = false;

            const resumeTime = this.transcodeStartTime + this.player.currentTime();
            await this.seekTranscode(resumeTime);

            this.$nextTick(() => {
                this.player.trigger('texttrackchange');
                this.player.trigger('resize');
                setTimeout(() => {
                    this.player.trigger('resize');
                }, 50);
            });
        },

        async selectAudioTrack(index) {
            if (!this.player || this.activeAudioIndex === index) {
                this.showAudioMenu = false;
                return;
            }

            this.activeAudioIndex = index;
            this.showAudioMenu = false;

            const resumeTime = this.transcodeStartTime + this.player.currentTime();
            await this.seekTranscode(resumeTime);
        },

        onImgError(event) {
            event.target.style.display = 'none'
        },

        toggleFullscreen() {
            const el = this.$el
            if (!document.fullscreenElement) {
                el.requestFullscreen?.()
            } else {
                document.exitFullscreen?.()
            }
        },

        startScrub(event) {
            this.seekToEvent(event)
            const onMove = (e) => this.seekToEvent(e)
            const onUp = () => {
                window.removeEventListener('mousemove', onMove)
                window.removeEventListener('mouseup', onUp)
            }
            window.addEventListener('mousemove', onMove)
            window.addEventListener('mouseup', onUp)
        },

        seekToEvent(event) {
            const video = this.$refs.video
            const bar = this.$refs.scrubber
            if (!video || !bar) return
            const rect = bar.getBoundingClientRect()
            const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
            video.currentTime = ratio * (video.duration || 0)
        },

        formatTime(seconds) {
            if (!isFinite(seconds) || seconds < 0) seconds = 0
            const h = Math.floor(seconds / 3600)
            const m = Math.floor((seconds % 3600) / 60)
            const s = Math.floor(seconds % 60)
            const pad = (n) => String(n).padStart(2, '0')
            return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
        },


        handleMouseMove(prompt=false) {
            if(prompt === true){
                this.nextPromptShown = true
            }

            this.showControls = true;
            this.resetControlsTimeout();
        },

        resetControlsTimeout() {
            if(this.controlsTimeout){ clearTimeout(this.controlsTimeout) }
            if ((this.playing && !this.showStreamMenu && !this.showEpisodeMenu) || this.mode === 'game') {
                this.controlsTimeout = setTimeout(() => {
                    this.showControls = false;
                }, 3000);
            }
        },

        clearSkipIntroForce() {
            if (this.skipIntroForceTimeout) {
                clearTimeout(this.skipIntroForceTimeout);
                this.skipIntroForceTimeout = null;
            }
            this.forceShowSkipIntro = false;
        },

        stopWatchingTimer(){
            if(this.stillWatchingTimeout){
                clearTimeout(this.stillWatchingTimeout);
            }
            if(this.willPauseTimeout){
                clearTimeout(this.willPauseTimeout);
            }
        },

        startWatchingTimer(){
            this.stopWatchingTimer()
            this.stillWatchingTimeout = setTimeout(() => {
                this.stillWatchingPrompt = true;
                this.willPauseTimeout = setTimeout(() => {
                    this.player.pause();
                }, 30000)
            }, 60000 * 90);
        },

        confirmStillWatching() {
            this.stillWatchingPrompt = false;
            this.stopWatchingTimer()
            this.startWatchingTimer()

            if (this.player.paused()) {
                this.player.play()
           }
        },

        pushLoaderMessage(text){
            if(this.$refs.stack){ this.$refs.stack.addMessage(text) }
        },

    }
}
</script>

<style lang="scss" scoped>
.theater{
    height: 100vh;
    width: 100vw;
    position: fixed;
    z-index: 99999;
    pointer-events: all;
    top: 0;
    left: 0;
    background-color: black;
    color: #fff;
    &.hide-cursor {
        cursor: none!important;
    }

    .loader{
        position: absolute;
        z-index: 9;

    }
    .top-nav {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 28px 40px;
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 100%);
        z-index: 3;
    }

    .bottom-bar {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 0 40px 24px;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%);
        z-index: 3;
    }

    .controls {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        padding-bottom: 8px;
    }

    .controls-group {
        display: flex;
        align-items: center;
        gap: 22px;

        &.right {
            justify-content: flex-end;
        }
    }

    .icon-btn.skip {
        position: relative;
    }

    .skip-label {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        pointer-events: none;
    }

    .title {
        justify-self: center;
        font-size: 17px;
        font-weight: 600;
        letter-spacing: 0.2px;
        white-space: nowrap;
    }

    .content-loader{
        position: absolute;
        pointer-events: none;
        width: 100vw;
        height: 100vh;
        top:0;
        left:0;
        z-index: 99;
    }
}

.stream-menu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1;
    background: transparent;
    cursor: default;
}

</style>

<style lang="scss">
.desktop.theater{
    .stream-menu-enter-active,
    .stream-menu-leave-active {
        transition: opacity 0.16s ease, transform 0.16s ease;
    }

    .stream-menu-enter-from,
    .stream-menu-leave-to {
        opacity: 0;
        transform: translateX(-50%) translateY(6px) scale(0.96);
    }

    .icon-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        background: none;
        border: none;
        padding: 0;
        color: #fff;
        cursor: pointer;
        opacity: 0.9;
        transition: opacity 0.15s ease, transform 0.15s ease;

        svg {
            width: 28px;
            height: 28px;
        }

        &:hover {
            opacity: 1;
            transform: scale(1.05);
        }

        &:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 4px;
            border-radius: 4px;
        }
    }

    video{
        outline: none!important;
    }
    video:focus{
        outline: none!important;
    }
    .video-container:focus {
        outline: none!important;
    }

    .video-container, .iframe-container {
        width: 100vw;
        height: 100vh;
        z-index: 1;
        position: absolute;

        &.iframe-container{
            pointer-events:none;
        }
        iframe{
            width: 100%;
            pointer-events:none;
            height: 100%;
            margin: 0;
            padding: 0;
            border: none!important;
            outline: none!important;
        }
        .video-js {
            width: 100%;
            height: 100%;
            outline: none!important;

            .vjs-tech{
                object-fit: contain;
                outline: none!important;
            }

            &.vjs-fluid,
            &.vjs-16-9,
            &.vjs-4-3 {
                padding-top: 0 !important;
                outline: none!important;
                height: 100% !important;
            }
        }
    }
    .allowpointer{
        pointer-events: all!important;
    }

.toast{
  top: 20vh;
  left: 50vw;
  transform: translateX(-50%);
  position: absolute;
  padding: 1.5%;
  z-index: 9999999999;
  color: white;
  font-weight: bold;
  border-radius: 10px;
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
    &.success{
        background-color: rgba(46, 204, 113,1.0);
    }
    &.error{
      background-color: rgba(231, 76, 60,1);
    }
    svg{
        margin-right: 12px;
    }
}

}
</style>
